// ============================================================
// Der Chip „Freigabe offen" / „Abgelehnt" (SPEC §36.9) — EINE Komponente für
// Galerie-Kachel, Board-Karte und Kartenkarte.
//
// Warum ein eigener Baustein und nicht dreimal ein `<Chip>` mit Klasse: die
// drei Orte sollen dasselbe sagen, im selben Ton, mit demselben Titel. Drei
// Abschriften wären drei Gelegenheiten, dass eine davon beim nächsten Wort-
// oder Farbwechsel stehen bleibt.
//
// LEISE mit Absicht: ein nicht freigegebenes Projekt ist kein Alarm, sondern
// ein Zustand, den genau zwei Gruppen sehen (die anlegende Person und wer
// freigibt). Der Ton kommt aus den vorhandenen Farbtokens (`--warn`,
// `--error`), gedämpft und ohne neue Farbe; die gestrichelte Kante sagt
// „vorläufig", bevor jemand das Wort liest.
//
// Der Grund steht auf einer DECKENDEN Fläche: auf der Galerie liegt der Chip
// über dem Titelbild, und ein durchscheinender Chip auf einem hellen Foto
// wäre unlesbar (dieselbe Regel wie `.ga2-fortschritt`).
// ============================================================
import type { CSSProperties } from "react";
import type { Freigabe } from "../../api/types";
import { useT } from "../../lib/texte";
import { Chip } from "./primitives";
import "./freigabechip.css";

export interface FreigabeChipProps {
  freigabe: Freigabe;
  className?: string;
  /** Für die Lage im Behälter (etwa `alignSelf` in einer Flexspalte) — die Farben
   *  bleiben beim Baustein. */
  style?: CSSProperties;
}

/**
 * Steht ein Chip da? Genau bei `offen` und `abgelehnt` — die EINE Frage, die
 * Galerie, Board und Kartenkarte stellen, bevor sie Platz für ihn machen.
 *
 * Bewusst als Aufzählung der zwei Zustände und nicht als `!== "freigegeben"`:
 * Backend und Oberfläche rollen getrennt aus, und ein `/stand` von vor §36
 * trägt den Schlüssel noch nicht. Dann soll gar nichts erscheinen — nicht ein
 * leerer Chip auf jeder Kachel, bis das Backend nachzieht.
 */
export function nichtFreigegeben(freigabe: Freigabe): boolean {
  return freigabe === "offen" || freigabe === "abgelehnt";
}

/** `null` bei `freigegeben` — ein Chip für den Normalfall wäre Rauschen auf jeder Kachel. */
export function FreigabeChip({ freigabe, className = "", style }: FreigabeChipProps) {
  const { t } = useT();
  if (!nichtFreigegeben(freigabe)) return null;
  return (
    <Chip
      icon={freigabe === "offen" ? "clock" : "x-circle"}
      title={t.freigabe.chipTitel}
      className={`fg-chip fg-chip-${freigabe} ${className}`.trim()}
      style={style}
    >
      {t.freigabe.zustand[freigabe]}
    </Chip>
  );
}
